/**
 * MLS-DUAL-001 — dual-workspace container for MLS fulfillment (post-Phase-4
 * UI refinement, Aug 2026). Owns the shared mls-search fetch, renders the
 * compact component summary + MLS SEARCH | TAX SEARCH tabs, and the MLS
 * workspace (component actions + Lien Search Report form + supporting docs).
 * The Tax workspace content itself stays inside TaxCertificatePanel — this
 * component only reports the active tab. Backend guards stay authoritative.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, Circle, PlayCircle, HelpCircle, PauseCircle, Mail, Lock, FileText, AlertTriangle } from 'lucide-react';
import { MlsWorkflowActions, ReopenControl, AssignMenu } from './MlsProgressCard';
import MlsSearchPanel from './MlsSearchPanel';
import { MlsParcelSelector, mlsParcelList } from './MlsParcelSelector';
import { toast } from 'sonner';
import { COMPONENT_LABELS, MAILAWAY_ACTIVE_STATUSES, isMlsManagerRole } from './rollupConfig';
import { useAuth } from '../../App';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  complete: { icon: CheckCircle2, cls: 'text-green-600', label: 'Complete' },
  in_progress: { icon: PlayCircle, cls: 'text-blue-600', label: 'In Progress' },
  clarification: { icon: HelpCircle, cls: 'text-orange-500', label: 'Clarification' },
  // MLS-STATUS-LABELS-001 (Sep 2026): internal `pending` = not started → "Open";
  // internal `on_hold` = explicitly paused by the processor → "Pending". Backend literals unchanged.
  on_hold: { icon: PauseCircle, cls: 'text-amber-600', label: 'Pending' },
  qc_review: { icon: Lock, cls: 'text-indigo-600', label: 'QC Review' },   // MLS-COMPONENT-QC-001
  pending: { icon: Circle, cls: 'text-gray-400', label: 'Open' },
};

const SummaryChip = ({ component, cdoc, orderId, onChanged, canManage }) => {
  const status = cdoc?.status || 'pending';
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <div className="mls-sum-card flex items-center gap-2.5 min-w-0 rounded-xl border border-indigo-100 bg-white px-3 py-2 shadow-sm" data-testid={`mls-summary-${component}`}>
      <span className="mls-tile bg-indigo-50 text-indigo-600"><FileText className="w-4 h-4" /></span>
      <span className="text-[13px] font-semibold text-slate-900">{COMPONENT_LABELS[component]}</span>
      <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${meta.cls}`}><Icon className="w-3.5 h-3.5" />{meta.label}</span>
      <span className="text-[11px] text-slate-500 truncate" data-testid={`mls-summary-${component}-assignee`}>
        · {cdoc?.assignee_name || 'Unassigned'}
      </span>
      {canManage && status !== 'complete' && (
        <AssignMenu orderId={orderId} component={component} onChanged={onChanged}
          currentAssignee={cdoc?.assignee_id} />
      )}
      {/* WF-CONSOLIDATE: manager-only reopen relocated beside the chip */}
      {canManage && status === 'complete' && (
        <ReopenControl orderId={orderId} component={component} onChanged={onChanged} />
      )}
    </div>
  );
};

export default function MlsDualSection({ orderId, orderData, readOnly = false, onRollupChange, activeTab = 'mls', onTabChange, refreshKey = 0 }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [maOpenSignal, setMaOpenSignal] = useState(0);
  // MLS-PARCELS-001: active MLS parcel (1 = mls_search, 2+ = mls_parcels[n-2])
  const [mlsParcelIdx, setMlsParcelIdx] = useState(1);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/orders/${orderId}/mls-search`);
      setData(res.data);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [orderId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // WF-CONSOLIDATE: parent (TaxCertificatePanel) bumps refreshKey after the
  // Tax component completes through the Tax Workflow Actions section.
  useEffect(() => { if (refreshKey) fetchData(); }, [refreshKey, fetchData]);

  // MLS-PARCELS-001: the top "Parcel x of N / + Add Parcel" bar (TaxCertificatePanel)
  // is component-aware on dual orders — while the MLS tab is active it talks to us
  // through window events instead of touching the Tax parcels[].
  const mlsParcels = mlsParcelList(data);
  const mlsParcelCount = mlsParcels.length;
  const mlsWritable = (() => {
    if (readOnly || !data || ['complete', 'completed'].includes((orderData?.status || '').toLowerCase())) return false;
    const cdoc = (data.components || {}).mls || {};
    if ((cdoc.status || 'pending') === 'complete' || !cdoc.assignee_id) return false;
    return isMlsManagerRole(user?.role) || cdoc.assignee_id === user?.id;
  })();
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('mls-parcel-state', {
      detail: { index: Math.min(mlsParcelIdx, mlsParcelCount), count: mlsParcelCount, writable: mlsWritable },
    }));
  }, [mlsParcelIdx, mlsParcelCount, mlsWritable, activeTab]);
  useEffect(() => {
    const onAdd = async () => {
      if (!mlsWritable) return;
      try {
        const res = await axios.post(`${API}/api/orders/${orderId}/components/mls/parcels`);
        toast.success(`MLS PCL ${res.data.parcel_index} added`);
        await fetchData();
        setMlsParcelIdx(res.data.parcel_index);
      } catch (e) {
        const d = e.response?.data?.detail;
        toast.error(typeof d === 'string' ? d : 'Failed to add MLS parcel');
      }
    };
    const onNav = (e) => setMlsParcelIdx((i) => Math.min(Math.max(1, i + (e.detail?.delta || 0)), mlsParcelCount));
    window.addEventListener('mls-parcel-add', onAdd);
    window.addEventListener('mls-parcel-nav', onNav);
    return () => { window.removeEventListener('mls-parcel-add', onAdd); window.removeEventListener('mls-parcel-nav', onNav); };
  }, [orderId, mlsWritable, mlsParcelCount, fetchData]);
  useEffect(() => { if (mlsParcelIdx > mlsParcelCount) setMlsParcelIdx(Math.max(1, mlsParcelCount)); }, [mlsParcelIdx, mlsParcelCount]);
  // Keep the local parcel cache in sync with autosaves so switching parcels shows saved data.
  // MLS-TAX-CERT-GENERATION-001: safe retry (idempotent server-side)
  const [certRetryBusy, setCertRetryBusy] = useState(false);
  const retryTaxCertificate = useCallback(async () => {
    setCertRetryBusy(true);
    try {
      const res = await axios.post(`${API}/api/orders/${orderId}/components/tax/certificate/regenerate`);
      const c = res.data?.certificate || {};
      toast.success(c.status === 'existing' ? `Tax Certificate already on file: ${c.filename}` : `Tax Certificate generated: ${c.filename}`);
      await fetchData();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Tax Certificate generation failed'), { duration: 8000 });
      await fetchData();
    } finally { setCertRetryBusy(false); }
  }, [orderId, fetchData]);

  const onMlsParcelSaved = useCallback((idx, doc, gates) => {
    setData((d) => {
      if (!d) return d;
      // MLS-WORKFLOW-VALIDATION-001: carry the server-computed gates returned by the save
      const g = gates && Array.isArray(gates.mls_completion_blockers)
        ? { mls_parcel_present: gates.mls_parcel_present !== false, mls_completion_blockers: gates.mls_completion_blockers,
            ...(gates.mls_mailaway_sections_ready ? { mls_mailaway_sections_ready: gates.mls_mailaway_sections_ready } : {}) } : {};
      if (idx === 1) return { ...d, ...g, mls_search: { ...(d.mls_search || {}), ...doc } };
      const arr = [...(d.mls_parcels || [])];
      arr[idx - 2] = { ...(arr[idx - 2] || {}), ...doc, parcel_index: idx };
      return { ...d, ...g, mls_parcels: arr };
    });
  }, []);

  useEffect(() => {
    if (data?.component_rollup) onRollupChange?.(data.component_rollup, data.components);
  }, [data, onRollupChange]);
  // MLS-ACCESS-CTRL: if a processor lands on a tab they don't own, switch
  // them to the component actually assigned to them (one-way, no loops).
  useEffect(() => {
    if (!data || readOnly) return;
    if (['complete', 'completed'].includes((orderData?.status || '').toLowerCase())) return;
    if (isMlsManagerRole(user?.role)) return;
    const comps = data.components || {};
    const mine = (t) => comps[t]?.assignee_id === user?.id;
    const other = activeTab === 'mls' ? 'tax' : 'mls';
    if (!mine(activeTab) && mine(other)) onTabChange?.(other);
  }, [data, activeTab, readOnly, orderData?.status, user, onTabChange]);

  if (failed) {
    return (
      <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-center justify-between gap-3"
        data-testid="mls-dual-load-error">
        <span className="text-xs text-amber-800 font-medium">
          Could not load the MLS component summary — the MLS workspace is temporarily unavailable.
        </span>
        <button type="button" onClick={fetchData} data-testid="mls-dual-retry-btn"
          className="text-xs font-semibold text-amber-900 underline hover:no-underline flex-shrink-0">
          Retry
        </button>
      </div>
    );
  }
  if (!data) return null;

  const components = data.components || {};
  const rollup = data.component_rollup;
  const orderComplete = ['complete', 'completed'].includes((orderData?.status || '').toLowerCase());
  const isReadOnly = readOnly || orderComplete;
  const doneCount = ['tax', 'mls'].filter((c) => components[c]?.status === 'complete').length;
  // MLS-MAILAWAY-P2: MLS rows come fresh from the mls-search fetch (own
  // component-scoped list); Tax rows still come from orderData.
  const mlsMailAway = data.mls_mail_away_requests || [];
  const mlsActiveMa = mlsMailAway.filter((r) => MAILAWAY_ACTIVE_STATUSES.includes(r.status)).length;
  const maCount = (comp) => (orderData?.mail_away_requests || []).filter(
    (r) => (r.component || 'tax') === comp && MAILAWAY_ACTIVE_STATUSES.includes(r.status)
  ).length;

  const tabBtn = (tab, label) => {
    const active = activeTab === tab;
    const cdoc = components[tab] || {};
    const st = cdoc.status || 'pending';
    // MLS-ACCESS-CTRL: processors may only open the component assigned to
    // them; the other tab stays VISIBLE but disabled so they can see the
    // order has two components. Managers/read-only oversight keep both.
    const enabled = isReadOnly || isMlsManagerRole(user?.role) || cdoc.assignee_id === user?.id;
    return (
      <button type="button" data-testid={`mls-workspace-tab-${tab}`}
        onClick={() => { if (enabled) onTabChange?.(tab); }}
        aria-disabled={!enabled}
        data-tab-disabled={!enabled ? 'true' : 'false'}
        title={enabled ? undefined
          : (cdoc.assignee_name ? `Assigned to ${cdoc.assignee_name} — you don't have access to this component` : 'Not currently assigned — no access')}
        data-active={active ? 'true' : 'false'}
        className={`mls-tab-pill flex-1 sm:flex-none sm:min-w-[170px] px-5 py-2 text-xs font-bold tracking-wide rounded-lg border ${
          active
            ? 'bg-white text-indigo-700 border-indigo-200'
            : enabled
              ? 'bg-transparent text-slate-500 border-transparent hover:text-indigo-600 hover:bg-white/60'
              : 'bg-transparent text-slate-400 border-transparent opacity-60 cursor-not-allowed'
        }`}>
        <span className="inline-flex items-center gap-1.5">
          {st === 'complete'
            ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
            : <span className={`w-2 h-2 rounded-full ${st === 'in_progress' ? 'bg-blue-500' : st === 'clarification' ? 'bg-orange-400' : st === 'on_hold' ? 'bg-amber-500' : 'bg-gray-300'}`} />}
          {label}
          {!enabled && <Lock className="w-3 h-3 text-gray-400" />}
        </span>
      </button>
    );
  };

  // MLS-COMPONENT-LOCK (UAT Aug 2026): a component workspace is editable
  // only when that component is assigned — and, for non-manager users,
  // assigned to them. Returns null | 'unassigned' | 'other'.
  const componentLock = (c) => {
    const cdoc = components[c] || {};
    if ((cdoc.status || 'pending') === 'complete') return null;
    if (!cdoc.assignee_id) return 'unassigned';
    if (isMlsManagerRole(user?.role)) return null;
    return cdoc.assignee_id === user?.id ? null : 'other';
  };


  return (
    <div data-testid="mls-dual-section" className="mb-4">
      {/* Compact dual-component summary */}
      <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 px-3 py-2.5 mb-3 flex items-center justify-between flex-wrap gap-x-4 gap-y-2"
        data-testid="mls-component-summary">
        <div className="flex items-center flex-wrap gap-x-3 gap-y-2">
          <SummaryChip component="mls" cdoc={components.mls} orderId={orderId}
            onChanged={fetchData} canManage={!isReadOnly && isMlsManagerRole(user?.role)} />
          {mlsActiveMa > 0 && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-[10px] font-semibold"
              data-testid="mls-summary-mailaway-chip"
              title={`${mlsActiveMa} active MLS Mail-Away request(s) — must be received or cancelled before the MLS component can complete`}>
              <Mail className="w-3 h-3" /> Mail-Away ×{mlsActiveMa}
            </span>
          )}
          <span className="hidden sm:inline h-6 w-px bg-indigo-200" />
          <SummaryChip component="tax" cdoc={components.tax} orderId={orderId}
            onChanged={fetchData} canManage={!isReadOnly && isMlsManagerRole(user?.role)} />
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-indigo-100 bg-white px-3 py-2 shadow-sm">
          <div className="flex flex-col items-end gap-1 min-w-[120px]">
            <span className="text-xs font-bold text-indigo-900" data-testid="mls-progress-title">
              {doneCount} of 2 Complete
            </span>
            <div className="mls-progress-track w-full" aria-hidden="true"><div className="mls-progress-bar" style={{ width: `${(doneCount / 2) * 100}%` }} /></div>
          </div>
          {(rollup === 'ready_to_complete' || rollup === 'complete') && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-300 text-green-700 text-[11px] font-semibold"
              data-testid="mls-ready-indicator">
              <CheckCircle2 className="w-3 h-3" />
              {rollup === 'complete' ? 'Complete' : 'Ready for QC/Completion'}
            </span>
          )}
        </div>
      </div>

      {/* MLS SEARCH | TAX SEARCH workspace tabs */}
      <div className="flex items-center gap-1 rounded-xl bg-slate-100/80 border border-slate-200 p-1" data-testid="mls-workspace-tabs">
        {tabBtn('mls', 'MLS SEARCH')}
        {tabBtn('tax', 'TAX SEARCH')}
      </div>

      {/* MLS workspace — kept mounted so autosave state survives tab switches.
          WF-CONSOLIDATE (Aug 23, 2026): no strip under the tabs — the single
          authoritative MLS action area is the Workflow Actions section at the
          bottom of the MLS form. */}
      <div className={activeTab === 'mls' ? 'pt-3' : 'hidden'} data-testid="mls-search-workspace">
        {/* MLS-HOLD-P1: pending reason visible in the workspace for everyone */}
        {components.mls?.status === 'on_hold' && (
          <div className="mls-lock-banner mls-reveal mt-1 rounded-xl border border-amber-200 px-3 py-2 text-[13px] text-amber-900 shadow-sm"
            data-testid="mls-pending-workspace-banner">
            <span className="font-semibold inline-flex items-center gap-1.5">
              <PauseCircle className="w-4 h-4" /> MLS Search is Pending
            </span>
            <span> — {components.mls.pending_reason || 'No reason recorded'}</span>
            {components.mls.pending_note && <span className="text-amber-800"> · {components.mls.pending_note}</span>}
            {components.mls.pending_by_name && (
              <span className="text-xs text-amber-700"> (by {components.mls.pending_by_name})</span>
            )}
          </div>
        )}
        {!isReadOnly && componentLock('mls') && (
          <div className="mls-lock-banner mls-reveal mt-1 rounded-xl border border-amber-200 px-3 py-2 text-[13px] text-amber-900 font-medium flex items-center gap-2.5 shadow-sm"
            data-testid="mls-component-locked-banner">
            <span className="mls-tile bg-amber-400 text-white" style={{ width: 24, height: 24, borderRadius: 9999 }}><AlertTriangle className="w-3.5 h-3.5" /></span>
            {componentLock('mls') === 'unassigned'
              ? 'MLS Search is not currently assigned.'
              : `MLS Search is assigned to ${components.mls?.assignee_name || 'another user'} — read-only for you.`}
          </div>
        )}
        <div className="mt-3">
          <MlsSearchPanel
            key={`mls-pcl-${Math.min(mlsParcelIdx, mlsParcelCount)}`}
            orderId={orderId}
            header={data.header}
            mlsSearch={mlsParcels[Math.min(mlsParcelIdx, mlsParcelCount) - 1]}
            parcelIndex={Math.min(mlsParcelIdx, mlsParcelCount)}
            parcelSelector={(
              <MlsParcelSelector orderId={orderId} parcels={mlsParcels} active={Math.min(mlsParcelIdx, mlsParcelCount)}
                onSelect={setMlsParcelIdx} readOnly={isReadOnly || !!componentLock('mls')} onChanged={fetchData} />
            )}
            onSaved={onMlsParcelSaved}
            mlsStatus={components.mls?.status}
            clientProduct={orderData?.client_product}
            readOnly={isReadOnly || !!componentLock('mls')}
            onChanged={fetchData}
          />
        </div>
        {/* MLS-WF-UNIFY-001: the standalone "MLS Mail-Away Requests" card is folded
            into Workflow Actions → Mail-Away (summary + Manage Mail-Away → same modal). */}
        <MlsWorkflowActions
          orderId={orderId}
          cdoc={components.mls}
          mailAwayRequests={mlsMailAway}
          readOnly={isReadOnly || !!componentLock('mls')}
          onChanged={fetchData}
          parcelPresent={data.mls_parcel_present !== false}
          completionBlockers={data.mls_completion_blockers || []}
          mailAwaySectionsReady={data.mls_mailaway_sections_ready || null}
        />
      </div>

      {/* Tax workspace — WF-CONSOLIDATE: no duplicate action strip. The
          existing Tax Workflow Actions section (bottom of the Tax form,
          rendered by TaxCertificatePanel below) is the single authoritative
          Tax action area; its Complete button is dual-component aware. */}
      <div className={activeTab === 'tax' ? 'pt-3' : 'hidden'} data-testid="mls-tax-component-bar">
        {/* MLS-TAX-CERT-GENERATION-001 / UI-CLEANUP-001: the generated certificate lives under Files → Tax;
            only an actual generation failure is surfaced here (with a safe retry). */}
        {components.tax?.status === 'complete' && (() => {
          const cert = data.tax_certificate || {};
          const canRetry = !isReadOnly && !componentLock('tax');
          if (cert.status === 'generated' && cert.file_id) return null;
          return (
            <div className="mls-reveal mt-1 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-900 flex flex-wrap items-center gap-x-3 gap-y-1 shadow-sm"
              data-testid="mls-tax-certificate-banner" data-cert-status={cert.status || 'missing'}>
              <span className="font-semibold inline-flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Tax Certificate PDF {cert.status === 'failed' ? 'generation failed' : 'not generated'}</span>
              {cert.error && <span className="text-rose-800" data-testid="mls-tax-certificate-error">{cert.error}</span>}
              {canRetry && (
                <button type="button" disabled={certRetryBusy}
                  className="ml-auto text-xs font-semibold rounded-md border border-rose-300 bg-white px-2.5 py-1 text-rose-800 hover:bg-rose-100 disabled:opacity-50"
                  data-testid="mls-tax-certificate-retry-btn" onClick={retryTaxCertificate}>
                  {certRetryBusy ? 'Generating…' : 'Retry certificate'}
                </button>
              )}
            </div>
          );
        })()}
        {!isReadOnly && componentLock('tax') && (
          <div className="mls-lock-banner mls-reveal mt-1 rounded-xl border border-amber-200 px-3 py-2 text-[13px] text-amber-900 font-medium flex items-center gap-2.5 shadow-sm"
            data-testid="tax-component-locked-banner">
            <span className="mls-tile bg-amber-400 text-white" style={{ width: 24, height: 24, borderRadius: 9999 }}><AlertTriangle className="w-3.5 h-3.5" /></span>
            {componentLock('tax') === 'unassigned'
              ? 'Tax Search is not currently assigned.'
              : `Tax Search is assigned to ${components.tax?.assignee_name || 'another user'} — read-only for you.`}
          </div>
        )}
      </div>
    </div>
  );
}
